/**
 * "My Wi-Fi" — what this browser keeps about its own connection after signing in:
 * the key from sign-in (authenticate → guestToken, which shows and ends this one
 * connection — backend portal.controller myWifi*), the branch (to show the page in
 * its branding when the guest opens the portal again without gateway parameters),
 * and the gateway's own logout address.
 *
 * localStorage, not sessionStorage: the guest comes back to it later, in another tab.
 */

export const GUEST_SESSION_KEY = 'hotwifi.guestSession';
// Longer than any stay is not needed: a key the server no longer knows is dropped.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function storage() {
    try { return window.localStorage; } catch { return null; }
}

const isHttpUrl = (value) => {
    if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return false;
    try { new URL(value); return true; } catch { return false; }
};

/**
 * The address on the gateway that ends this device's connection when the browser
 * opens it — the way out that works on every gateway, whether or not it takes
 * HotWiFi's Disconnect-Request:
 *   Ruijie   — its own logout_url (one of the parameters it sends to the portal)
 *   MikroTik — /logout on the hotspot address its login link points at
 *   Nomadix  — none; HotWiFi logs the guest out over the gateway's XML interface
 */
export function gatewayLogoutUrl(params) {
    const ruijie = params.get('logout_url');
    if (isHttpUrl(ruijie)) return ruijie;
    const mikrotik = params.get('link-logout');
    if (isHttpUrl(mikrotik)) return mikrotik;
    const login = params.get('link-login-only') || params.get('link-login');
    if (isHttpUrl(login)) {
        const url = new URL(login);
        url.pathname = '/logout';
        url.search = '';
        url.hash = '';
        return url.toString();
    }
    return null;
}

export function saveGuestSession({ token, branchId, logoutUrl }, now = Date.now()) {
    if (!token) return;
    try {
        storage()?.setItem(GUEST_SESSION_KEY, JSON.stringify({
            token, branchId: branchId ? String(branchId) : null, logoutUrl: logoutUrl || null, savedAt: now,
        }));
    } catch { /* storage full/blocked — the page simply won't remember the connection */ }
}

export function loadGuestSession(now = Date.now()) {
    let saved = null;
    try { saved = JSON.parse(storage()?.getItem(GUEST_SESSION_KEY) || 'null'); } catch { saved = null; }
    if (!saved || typeof saved.token !== 'string' || !saved.savedAt || now - saved.savedAt > MAX_AGE_MS) return null;
    return saved;
}

/** The guest pressed Disconnect: the next visit shows the session summary once. */
export function markGuestDisconnected(now = Date.now()) {
    const saved = loadGuestSession(now);
    if (!saved) return;
    try { storage()?.setItem(GUEST_SESSION_KEY, JSON.stringify({ ...saved, disconnectedAt: now })); } catch { /* blocked */ }
}

export function clearGuestSession() {
    try { storage()?.removeItem(GUEST_SESSION_KEY); } catch { /* blocked */ }
}

// ── How the page writes the numbers ────────────────────────────────────

/** 4358 s → "1h 12m 38s"; 95 s → "1m 35s". */
export function formatDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}

/** 642668954 → "612.9 MB" (binary units, one decimal). */
export function formatBytes(bytes) {
    const n = Math.max(0, Number(bytes) || 0);
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = n;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
    return unit === 0 ? `${value} B` : `${value.toFixed(1)} ${units[unit]}`;
}

/** The rows of the session summary, as the connected / disconnected screens list them. */
export function summaryRows(summary) {
    if (!summary) return [];
    return [
        { label: 'Signed in as', value: summary.signedInAs },
        { label: 'Time online', value: formatDuration(summary.durationSec) },
        { label: 'Downloaded', value: formatBytes(summary.downloadBytes) },
        { label: 'Uploaded', value: formatBytes(summary.uploadBytes) },
    ];
}
